/**
 * WorkerTimers — arka plan sekmesinde KISILMAYAN zamanlayıcılar.
 *
 * PROD-KANIT (2026-08 canlı denetim): 35 dakikada 61 yeniden bağlanma, 0
 * başarılı RESUME. Kök sebep: tarayıcılar arka plan sekmelerinde ana-thread
 * setTimeout/setInterval'i kısar (Chrome "intensive throttling": 5dk+ gizli
 * sekmede timer'lar EN FAZLA dakikada bir çalışır). GatewayV2Socket'in ~33sn'lik
 * (interval*0.8) heartbeat tick'i bu kısıtlamada 60sn+'ye kayıyor; Erlang
 * gateway 45sn sessizlikte 4009 session_timeout ile kapatıyor (4009 → istemci
 * session'ı temizler → her dönüş sıfırdan IDENTIFY → churn).
 *
 * ÇÖZÜM: Web Worker içindeki timer'lar bu kısıtlamadan MUAF (worker'lar arka
 * planda da normal çalışır). Bu modül, Blob-URL'den inline bir worker kurup
 * setTimeout/clearTimeout yüzeyini oradan sağlar; Worker kurulamazsa (SSR,
 * CSP worker-src engeli, test ortamı) ana-thread setTimeout'a sessizce düşer
 * (davranış aynı, yalnız arka plan garantisi kaybolur — eski durumla eşdeğer).
 *
 * Worker beklenmedik şekilde ölürse (onerror) bekleyen tüm zamanlayıcılar
 * kalan süreleriyle ana-thread'e devredilir — heartbeat/reconnect zinciri
 * hiçbir arıza modunda sessizce durmaz.
 */

type TimerCallback = () => void;

/** Worker içinde koşan kaynak — ana-thread'den {set|clear} mesajı alır, süre dolunca {id} yollar. */
const WORKER_SOURCE = `
var timers = {};
self.onmessage = function (e) {
  var msg = e.data || {};
  if (msg.type === "set") {
    timers[msg.id] = setTimeout(function () {
      delete timers[msg.id];
      self.postMessage({ id: msg.id });
    }, msg.delay);
  } else if (msg.type === "clear") {
    var t = timers[msg.id];
    if (t !== undefined) {
      clearTimeout(t);
      delete timers[msg.id];
    }
  }
};
`;

interface PendingTimer {
  callback: TimerCallback;
  /** Worker ölürse ana-thread'e devretmek için mutlak son tarih (Date.now() tabanlı). */
  expiresAt: number;
}

class WorkerTimerRegistry {
  private worker: Worker | null = null;
  /** Worker kurulumu bir kez başarısız olduysa bir daha denenmez (kalıcı fallback). */
  private workerUnavailable = false;
  private nextId = 1;
  /** Worker'da bekleyen zamanlayıcılar (id → callback + son tarih). */
  private pending = new Map<number, PendingTimer>();
  /** Fallback (ana-thread) zamanlayıcıları (id → gerçek setTimeout tanıtıcısı). */
  private fallbackHandles = new Map<number, ReturnType<typeof setTimeout>>();

  /** Worker-tabanlı setTimeout — dönen id YALNIZ bu modülün clearTimeout'una verilebilir. */
  setTimeout(callback: TimerCallback, delayMs: number): number {
    const id = this.nextId++;
    const worker = this.ensureWorker();
    if (worker) {
      this.pending.set(id, { callback, expiresAt: Date.now() + delayMs });
      worker.postMessage({ type: "set", id, delay: delayMs });
    } else {
      this.armFallback(id, callback, delayMs);
    }
    return id;
  }

  clearTimeout(id: number | null | undefined): void {
    if (id == null) return;
    if (this.pending.delete(id)) {
      this.worker?.postMessage({ type: "clear", id });
      return;
    }
    const handle = this.fallbackHandles.get(id);
    if (handle !== undefined) {
      clearTimeout(handle);
      this.fallbackHandles.delete(id);
    }
  }

  private armFallback(id: number, callback: TimerCallback, delayMs: number): void {
    const handle = setTimeout(() => {
      this.fallbackHandles.delete(id);
      callback();
    }, delayMs);
    this.fallbackHandles.set(id, handle);
  }

  private ensureWorker(): Worker | null {
    if (this.workerUnavailable) return null;
    if (this.worker) return this.worker;
    if (
      typeof Worker === "undefined" ||
      typeof Blob === "undefined" ||
      typeof URL === "undefined" ||
      typeof URL.createObjectURL !== "function"
    ) {
      this.workerUnavailable = true;
      return null;
    }
    try {
      const blobUrl = URL.createObjectURL(new Blob([WORKER_SOURCE], { type: "text/javascript" }));
      const worker = new Worker(blobUrl);
      // Worker script'i yüklendikten sonra URL'e ihtiyaç kalmaz — sızıntı önle.
      URL.revokeObjectURL(blobUrl);
      worker.onmessage = (event: MessageEvent) => {
        const firedId = (event.data as { id?: number } | null)?.id;
        if (typeof firedId !== "number") return;
        const entry = this.pending.get(firedId);
        if (!entry) return; // yarış: süre dolarken clear edildi — yut
        this.pending.delete(firedId);
        entry.callback();
      };
      worker.onerror = () => {
        // Worker öldü: bekleyenleri KALAN süreleriyle ana-thread'e devret ki
        // heartbeat/reconnect zinciri hiçbir arıza modunda kopmasın.
        this.migrateToFallback();
      };
      this.worker = worker;
      return worker;
    } catch {
      // CSP worker-src engeli vb. — kalıcı ana-thread fallback.
      this.workerUnavailable = true;
      return null;
    }
  }

  private migrateToFallback(): void {
    const orphaned = [...this.pending.entries()];
    this.pending.clear();
    try {
      this.worker?.terminate();
    } catch {
      /* zaten ölü olabilir */
    }
    this.worker = null;
    this.workerUnavailable = true;
    const now = Date.now();
    for (const [id, entry] of orphaned) {
      this.armFallback(id, entry.callback, Math.max(0, entry.expiresAt - now));
    }
  }
}

/**
 * Tek paylaşılan kayıt — gateway yaşam-döngüsü zamanlayıcılarının tamamı
 * (heartbeat tick/ack + hello/resume/invalid-session/reconnect) buradan geçer.
 */
export const workerTimers = new WorkerTimerRegistry();
